import React from 'react';
import { 
  Paper, Typography, Box, Grid, Divider, LinearProgress,
  List, ListItem, ListItemIcon, ListItemText, Chip, useTheme
} from '@mui/material';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import BuildIcon from '@mui/icons-material/Build';
import SpeedIcon from '@mui/icons-material/Speed';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SmartToyIcon from '@mui/icons-material/SmartToy';

const RobotStatus = () => {
  const theme = useTheme();
  
  // Mock robot state data
  const robotData = {
    state: 'Unpacking',
    currentItem: 'Large Box #2453',
    estimatedCompletion: '00:04:32',
    gripperStatus: 'Closed - Object in grip',
    lastMaintenance: '28 days ago',
    efficiency: 87,
    temperature: 'Normal',
    errors: 0
  };
  
  const getStateColor = (state) => {
    switch (state) {
      case 'Idle': return 'info';
      case 'Unpacking': return 'success';
      case 'Error': return 'error';
      case 'Maintenance': return 'warning';
      default: return 'default';
    }
  };
  
  return (
    <Paper 
      elevation={10} 
      sx={{ 
        p: 0, 
        height: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        background: 'rgba(30, 30, 40, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.05)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SmartToyIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Robot Status
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ 
        p: 3, 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3,
          p: 2,
          borderRadius: '12px',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <SettingsRemoteIcon 
            sx={{ 
              mr: 2, 
              fontSize: 40, 
              color: robotData.state === 'Unpacking' ? '#4CAF50' : theme.palette.bmwAccent.midBlue,
              animation: robotData.state === 'Unpacking' ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { opacity: 0.7 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.7 }
              }
            }} 
          />
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Status
            </Typography>
            <Chip 
              label={robotData.state} 
              color={getStateColor(robotData.state)}
              sx={{ 
                fontWeight: 600,
                px: 1
              }}
            />
          </Box>
        </Box>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ 
              p: 2, 
              borderRadius: '12px', 
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              height: '100%'
            }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Task
              </Typography>
              <Typography variant="body1" fontWeight="600" sx={{ color: theme.palette.bmwAccent.lightBlue }}>
                {robotData.currentItem}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box sx={{ 
              p: 2, 
              borderRadius: '12px', 
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Estimated Completion
              </Typography>
              <Typography 
                variant="h5" 
                fontWeight="700" 
                sx={{ 
                  color: '#4CAF50',
                  textAlign: 'center',
                  my: 'auto'
                }}
              >
                {robotData.estimatedCompletion}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ 
              p: 2, 
              borderRadius: '12px', 
              bgcolor: 'rgba(0, 0, 0, 0.2)'
            }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Gripper Status
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {robotData.gripperStatus}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        <List sx={{ 
          bgcolor: 'rgba(0, 0, 0, 0.2)', 
          borderRadius: '12px', 
          p: 2
        }}>
          <ListItem 
            sx={{ 
              px: 1, 
              py: 0.75,
              borderRadius: '8px',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <BuildIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Last Maintenance" 
              secondary={robotData.lastMaintenance} 
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'body2', sx: { color: 'text.secondary' } }}
            />
          </ListItem>
          
          <ListItem 
            sx={{ 
              px: 1, 
              py: 0.75,
              borderRadius: '8px',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <SpeedIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Efficiency" 
              primaryTypographyProps={{ variant: 'body2' }}
            />
            <Box sx={{ width: '40%', ml: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={robotData.efficiency} 
                color="primary"
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${theme.palette.bmwAccent.midBlue} 0%, ${theme.palette.bmwAccent.lightBlue} 100%)`,
                    borderRadius: 5
                  }
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ ml: 1, width: 30, fontWeight: 600 }}>
              {robotData.efficiency}%
            </Typography>
          </ListItem>
          
          <ListItem 
            sx={{ 
              px: 1, 
              py: 0.75,
              borderRadius: '8px', 
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AssignmentIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Temperature" 
              secondary={robotData.temperature} 
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ 
                variant: 'body2', 
                sx: { 
                  color: robotData.temperature === 'Normal' ? '#4CAF50' : 'text.secondary',
                  fontWeight: 600
                } 
              }}
            />
          </ListItem>
        </List>
      </Box>
    </Paper>
  );
};

export default RobotStatus; 